<template>
  <b-card no-body>
    <b-tabs v-model="tabIndex" card>
      <b-tab title="Дашборд" lazy>
        <finance-dashboard />
      </b-tab>
      <b-tab title="Недели" lazy>
        <finance-weeks-list />
      </b-tab>
      <b-tab title="Выплаты" lazy>
        <finance-payouts />
      </b-tab>
      <b-tab title="Окупаемость пакетов" lazy>
        <finance-packs-roi />
      </b-tab>

      <template #tabs-end>
        <li class="nav-item ml-auto d-flex align-items-center pr-1 finance-cache-status">
          <small
            v-if="isRefreshing"
            class="text-muted mr-1"
          >
            Обновление данных… обычно занимает несколько минут
          </small>
          <small
            v-else-if="cachedAt"
            class="text-muted mr-1"
            title="Данные вкладок берутся из кеша, который пересобирается раз в 2 часа"
          >
            Данные на {{ formatCachedAt(cachedAt) }}
          </small>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="isRefreshing"
            title="Пересобрать кеш из Planfix"
            @click="refresh"
          >
            <b-spinner v-if="isRefreshing" small class="mr-50" />
            <feather-icon v-else icon="RefreshCwIcon" class="mr-50" />
            Обновить
          </b-button>
        </li>
      </template>
    </b-tabs>
  </b-card>
</template>

<script>
import {
  BCard, BTabs, BTab, BButton, BSpinner,
} from 'bootstrap-vue'
import FinanceDashboard from '@/views/finance/FinanceDashboard.vue'
import FinanceWeeksList from '@/views/finance/FinanceWeeksList.vue'
import FinancePayouts from '@/views/finance/FinancePayouts.vue'
import FinancePacksRoi from '@/views/finance/FinancePacksRoi.vue'
import {
  ref, computed, watch, onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import financeStoreModule from '@/views/finance/financeStoreModule'

const TAB_NAMES = ['dashboard', 'weeks', 'payouts', 'packs-roi']
const STORE_MODULE_NAME = 'app-finance'

// Кнопка «Обновить» лишь ставит флаг на сервере; сборку делает планировщик (подхватывает за ≤1 мин),
// поэтому фронт поллит статус до смены updated_at.
const REFRESH_POLL_INTERVAL_MS = 5000
const REFRESH_POLL_TIMEOUT_MS = 15 * 60 * 1000

export default {
  components: {
    BCard, BTabs, BTab, BButton, BSpinner,
    FinanceDashboard, FinanceWeeksList, FinancePayouts, FinancePacksRoi,
  },
  setup(_, { root }) {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, financeStoreModule)

    const toast = useToast()

    const initialTab = TAB_NAMES.indexOf(root.$route.query.tab)
    const tabIndex = ref(initialTab >= 0 ? initialTab : 0)

    watch(tabIndex, value => {
      const name = TAB_NAMES[value]
      if (root.$route.query.tab === name) return
      root.$router.replace({ query: { ...root.$route.query, tab: name } })
    })

    const cachedAt = computed(() => store.state[STORE_MODULE_NAME].cachedAt)
    const isRefreshing = computed(() => store.state[STORE_MODULE_NAME].isRefreshing)

    const formatCachedAt = iso => new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })

    let pollTimer = null
    const stopPolling = () => {
      if (pollTimer) clearTimeout(pollTimer)
      pollTimer = null
    }

    const finishRefresh = () => {
      stopPolling()
      store.commit(`${STORE_MODULE_NAME}/setRefreshing`, false)
    }

    const pollUntilUpdated = (previousUpdatedAt, startedAt) => {
      pollTimer = setTimeout(() => {
        store.dispatch(`${STORE_MODULE_NAME}/fetchCacheStatus`)
          .then(response => {
            const status = (response.data && response.data.data) || {}
            const updated = status.updated_at && status.updated_at !== previousUpdatedAt
            if (updated) {
              store.commit(`${STORE_MODULE_NAME}/setCachedAt`, status.updated_at)
              finishRefresh()
              store.commit(`${STORE_MODULE_NAME}/bumpRefreshVersion`)
              toast({
                component: ToastificationContent,
                props: { title: 'Данные финансов обновлены', icon: 'CheckCircleIcon', variant: 'success' },
              })
              return
            }
            if (Date.now() - startedAt > REFRESH_POLL_TIMEOUT_MS) {
              finishRefresh()
              toast({
                component: ToastificationContent,
                props: {
                  title: 'Обновление затянулось — попробуйте перезагрузить страницу позже',
                  icon: 'AlertTriangleIcon',
                  variant: 'warning',
                },
              })
              return
            }
            pollUntilUpdated(previousUpdatedAt, startedAt)
          })
          .catch(() => pollUntilUpdated(previousUpdatedAt, startedAt))
      }, REFRESH_POLL_INTERVAL_MS)
    }

    const refresh = () => {
      if (isRefreshing.value) return
      store.commit(`${STORE_MODULE_NAME}/setRefreshing`, true)
      store.dispatch(`${STORE_MODULE_NAME}/requestRefresh`)
        .then(response => {
          const status = (response.data && response.data.data) || {}
          pollUntilUpdated(status.updated_at || cachedAt.value, Date.now())
        })
        .catch(err => {
          finishRefresh()
          toast({
            component: ToastificationContent,
            props: {
              title: err.response?.data?.message || 'Не удалось запросить обновление данных',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    onUnmounted(() => {
      stopPolling()
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    return {
      tabIndex, cachedAt, isRefreshing, formatCachedAt, refresh,
    }
  },
}
</script>

<style lang="scss" scoped>
.finance-cache-status {
  white-space: nowrap;
}
</style>
